// @ts-check

import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import { useDisabled } from "#ce/composables/useDisabled";
import { useHostElement } from "#ce/composables/useHostElement";
import { FORM_DISABLED_EVENT } from "#ce/framework/events";

/**
 * Reconciles a form control's disabled state from the two sources that govern a
 * form-associated custom element:
 *
 *  1. The element's own `disabled` attribute (presence-based; `disabled="false"`
 *     is normalized away at the boundary, so it never reaches here as truthy).
 *  2. An ancestor `<fieldset disabled>`, signalled by the native
 *     `formDisabledCallback` which the form-aware wrapper re-emits as the
 *     {@link FORM_DISABLED_EVENT} on the host. The event is only a change
 *     signal — the state itself is recomputed from the DOM, because desktop
 *     Safari fires the callback spuriously (see onFormDisabled below).
 *
 * The returned `disabled` ref is the single source of truth a component should bind
 * to its inner control, replacing a per-component `toBoolean(props.disabled)`.
 * Host-level disabled *styling* needs no JS at all — style `:host(:disabled)`.
 *
 * @param {{ disabled?: import("vue").MaybeRefOrGetter<*> }} [options]
 */
export function useFormControl(options) {
  const { disabled } = options ?? {};
  const host = useHostElement();

  // Own-attribute disabled state (presence-based), shared with non-form controls.
  const { disabled: ownDisabled } = useDisabled(disabled);

  // Native disabled state from formDisabledCallback (re-emitted as the event):
  // the element's overall native state — own attribute presence AND an ancestor
  // <fieldset disabled>. useEventListener auto-removes on unmount.
  const nativeDisabled = ref(false);

  useEventListener(host, FORM_DISABLED_EVENT, onFormDisabled);

  function onFormDisabled() {
    // Desktop Safari (WebKit) spuriously invokes formDisabledCallback(true) on a
    // form-associated element right after its tree is inserted in some host
    // arrangements (observed on the finder near-panel: no disabled attribute, no
    // ancestor fieldset, host.matches(':disabled') is false — yet the callback
    // reports disabled, permanently latching the control off). So the event is
    // treated only as a "disabledness may have changed" signal, and the state is
    // recomputed from the DOM — the callback runs after the DOM change it
    // reports, so the ground truth is already in place.
    nativeDisabled.value = hostIsNativelyDisabled();
  }

  // Only two things can natively disable a form-associated element: its own
  // disabled attribute, or an ancestor <fieldset disabled> — except when the
  // host sits inside that particular fieldset's first <legend>. The exemption
  // is per fieldset, so an outer disabled fieldset still disables a host inside
  // an inner fieldset's first legend.
  function hostIsNativelyDisabled() {
    if (!host) {
      return false;
    }

    if (host.hasAttribute("disabled")) {
      return true;
    }

    return ancestorsOf(host).some((ancestor) => isDisabledFieldset(ancestor) && !firstLegend(ancestor)?.contains(host));
  }

  onMounted(() => {
    // Seed from the current native state. `:host(:disabled)` covers both the own
    // attribute and an ancestor fieldset; harmlessly false where unsupported.
    if (host && typeof host.matches === "function" && host.matches(":disabled")) {
      nativeDisabled.value = true;
    }
  });

  return {
    disabled: computed(() => ownDisabled.value || nativeDisabled.value),
  };
}

function ancestorsOf(element) {
  const ancestors = [];

  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    ancestors.push(ancestor);
  }

  return ancestors;
}

function isDisabledFieldset(element) {
  return element.tagName === "FIELDSET" && element.hasAttribute("disabled");
}

// The first <legend> *child*, per the spec's exemption — found by scanning
// children rather than `:scope > legend` (jsdom's selector engine wrongly
// matches grandchild legends for that query).
function firstLegend(fieldset) {
  return Array.from(fieldset.children).find((child) => child.tagName === "LEGEND");
}
